/**
 * =============================================================================
 *  LOP CHON ANH DA NHUNG SAN TRONG TRANG
 * =============================================================================
 *
 *  Khac han lop chu thich: nhung anh nay KHONG PHAI do ta tao ra. Chung nam san
 *  trong content stream cua tai lieu goc, va moi thao tac tren chung deu la phau
 *  thuat vao noi dung do (core/content/).
 *
 * =============================================================================
 *  VI SAO LOP NAY KHONG TU BAT CU BAM DE CHON
 * =============================================================================
 *  Trang da co MOT lop bat thao tac (AnnotationLayer), va tep PageView.tsx mang
 *  san mot khoi ghi chu ve viec da tung co HAI lop chong nhau: ket qua la duong
 *  ve va duong thao tac khong bao gio gap nhau, va nguoi dung keo ra duoc mot
 *  hinh roi khong doi kich thuoc no duoc.
 *
 *  Nen lop nay KHONG dat mot be mat bat su kien thu hai len ca trang. Viec CHON
 *  do PageView lam - o do cu bam da di qua lop chu thich va khong trung gi. Lop
 *  nay chi dung ra tay nam va vung keo CHO DUNG MOT ANH DANG CHON. Ngoai vung
 *  do, no trong suot tuyet doi voi chuot.
 *
 * =============================================================================
 *  HINH HOC LAY TU core/annotations/shapes.ts, KHONG VIET LAI
 * =============================================================================
 *  Vi tri tay nam, ket qua keo tay nam, san kich thuoc nho nhat: tat ca goi dung
 *  cac ham ma lop chu thich goi. Mot ban sao thu hai o day se troi khoi ban kia
 *  - tay nam nam le mot chut, san kich thuoc khac mot chut - va nguoi dung se
 *  hoc mot quy tac roi thay no sai o lop ben canh.
 */
import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { PdfPoint, PdfRect } from '@pdfeditor/types';
import {
  HANDLE_CURSOR,
  MIN_OBJECT_SIZE_PT,
  handleAt,
  handlePoints,
  moveRect,
  normalizeRect,
  resizeRect,
  type HandleId,
} from '../core/annotations/shapes';
import { cssPointToPdf, pdfRectToCss, type PageGeometry } from './geometry';

/** Ban kinh bam trung tay nam, tinh bang DIEM cua trang - xem shapes.ts. */
const HANDLE_TOLERANCE_PT = 6;

export interface EmbeddedImageView {
  /** Ma on dinh: "p{trang}-img{so thu tu}". */
  id: string;
  rect: PdfRect;
  /**
   * Ly do KHONG sua duoc, hoac null.
   *
   * Anh van duoc ve o chon khi co ly do - nguoi dung phai thay duoc rang ho da
   * chon trung mot doi tuong that. Cai bi bo di la TAY NAM: mot tay nam keo
   * khong duoc la mot tay nam hong duoi mat nguoi dung, va shapes.ts da co san
   * ca mot khoi ghi chu ve dieu do (handleIdsFor).
   */
  refusal: string | null;
}

export interface EmbeddedImageLayerProps {
  images: readonly EmbeddedImageView[];
  geometry: PageGeometry;
  selectedId: string | null;
  /** Hop chu nhat moi sau mot cu keo. Da chuan hoa, da ap san kich thuoc. */
  onChangeRect: (id: string, rect: PdfRect) => void;
  /** Nguoi dung nha chuot - luc nay moi ghi nhan mot lenh. */
  onCommitRect: (id: string, rect: PdfRect) => void;
}

type Drag =
  | { kind: 'move'; id: string; from: PdfPoint; origin: PdfRect }
  | { kind: 'resize'; id: string; handle: HandleId; origin: PdfRect };

export function EmbeddedImageLayer({
  images,
  geometry,
  selectedId,
  onChangeRect,
  onCommitRect,
}: EmbeddedImageLayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  const selected = images.find((i) => i.id === selectedId) ?? null;

  const pointOf = useCallback(
    (event: ReactPointerEvent<HTMLElement>): PdfPoint => {
      const host = hostRef.current;
      if (!host) return { x: 0, y: 0 };
      const box = host.getBoundingClientRect();
      return cssPointToPdf(event.clientX - box.left, event.clientY - box.top, geometry);
    },
    [geometry],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!selected || selected.refusal !== null) return;
      // Cu bam nay THUOC VE lop nay. Khong de no roi xuong PageView, o do no se
      // duoc hieu la "bam vao cho trong" va bo chon dung cai vua bam.
      event.stopPropagation();
      event.preventDefault();
      const point = pointOf(event);
      const handle = handleAt(selected.rect, point, HANDLE_TOLERANCE_PT);
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag(
        handle
          ? { kind: 'resize', id: selected.id, handle, origin: selected.rect }
          : { kind: 'move', id: selected.id, from: point, origin: selected.rect },
      );
    },
    [selected, pointOf],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      event.stopPropagation();
      const point = pointOf(event);
      const next =
        drag.kind === 'move'
          ? moveRect(drag.origin, point.x - drag.from.x, point.y - drag.from.y)
          : resizeRect(drag.origin, drag.handle, point, {
              // Anh giu TI LE khi keo goc, giong moi trinh sua anh. Keo canh
              // giua thi khong - luc do nguoi dung dang co y bop mot chieu.
              keepAspect: !event.altKey,
              minSizePt: MIN_OBJECT_SIZE_PT,
            });
      onChangeRect(drag.id, normalizeRect(next));
    },
    [drag, pointOf, onChangeRect],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      event.stopPropagation();
      const current = images.find((i) => i.id === drag.id);
      setDrag(null);
      // GHI NHAN LENH KHI NHA TAY, khong phai moi khung hinh cua cu keo. Trong
      // luc keo, vi tri chi song trong trang thai tam cua ben goi.
      if (current) onCommitRect(drag.id, current.rect);
    },
    [drag, images, onCommitRect],
  );

  return (
    <div className="embeddedImageLayer" ref={hostRef} aria-hidden="true">
      {/* O CHON cua moi anh dang chon - chi de nhin, khong bat chuot. */}
      {selected && <Outline rect={selected.rect} geometry={geometry} refused={selected.refusal !== null} />}

      {/*
        VUNG BAT CHUOT chi ton tai khi co mot anh dang chon VA anh do sua duoc.
        Xem khoi ghi chu dau tep: khong co be mat bat su kien thu hai tren ca
        trang.
      */}
      {selected && selected.refusal === null && (
        <div
          className="embeddedImageGrab"
          style={grabStyle(selected.rect, geometry)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {handleList(selected.rect, geometry)}
        </div>
      )}
    </div>
  );
}

function Outline({
  rect,
  geometry,
  refused,
}: {
  rect: PdfRect;
  geometry: PageGeometry;
  refused: boolean;
}) {
  const css = pdfRectToCss(normalizeRect(rect), geometry);
  return (
    <div
      className={`embeddedImageOutline${refused ? ' refused' : ''}`}
      style={{
        left: `${css.left}px`,
        top: `${css.top}px`,
        width: `${css.width}px`,
        height: `${css.height}px`,
      }}
    />
  );
}

/**
 * Vung bat chuot NOI RONG ra ngoai hop anh dung bang dung sai tay nam.
 *
 * Neu no om khit hop anh, tay nam o goc chi bam duoc mot nua - nua kia nam
 * ngoai vung, va cu bam do roi xuong PageView thanh mot lan bo chon. Nguoi dung
 * nham vao tay nam va mat lua chon cua minh.
 */
function grabStyle(rect: PdfRect, geometry: PageGeometry) {
  const pad = HANDLE_TOLERANCE_PT * geometry.zoom;
  const css = pdfRectToCss(normalizeRect(rect), geometry);
  return {
    left: `${css.left - pad}px`,
    top: `${css.top - pad}px`,
    width: `${css.width + pad * 2}px`,
    height: `${css.height + pad * 2}px`,
  };
}

function handleList(rect: PdfRect, geometry: PageGeometry) {
  const pad = HANDLE_TOLERANCE_PT * geometry.zoom;
  const box = pdfRectToCss(normalizeRect(rect), geometry);
  const points = handlePoints(rect);
  return (Object.keys(points) as HandleId[]).map((id) => {
    const p = points[id];
    const css = pdfRectToCss({ x: p.x, y: p.y, width: 0, height: 0 }, geometry);
    return (
      <span
        key={id}
        className="embeddedImageHandle"
        style={{
          // Toa do TUONG DOI voi vung bat chuot, ma vung do da lui ra `pad`.
          left: `${css.left - box.left + pad}px`,
          top: `${css.top - box.top + pad}px`,
          cursor: HANDLE_CURSOR[id],
        }}
      />
    );
  });
}
